/**
 * The mode switcher's *Edit*, flown (`spec-onboarding-persistent-scene-edit-flight`): binds the
 * pure flight (`editFlight.ts`) to the live map, the plan read, the scene and the router.
 *
 * On the press the panels start to fade and the plan is read (1500 ms in all). With a target, the
 * other floors are made see-through and their programs built, then the camera flies from wherever it
 * is to Floor Plan's plan view while the heights flatten over its last 40%. On landing the map is
 * captured once into the handover, its camera handed on to Floor Plan, and the page navigates to
 * onboarding as before. Anything that cannot fly navigates straight away, as Edit always did.
 */
import { useCallback, useEffect, useReducer, useRef, useState } from 'react';
import type { Map as MapboxMap } from 'mapbox-gl';
import { useLocation, useNavigate } from 'react-router-dom';
import { getTwinPlanById, getTwinPlans } from '@repo/front-end-services';
import { useSpotlightStore } from '@repo/front-end-state';
import { readPlanView } from '@repo/front-end-utils';
import { flyToTarget } from '@repo/ui';
import type { FloorPlannerLayout } from '@repo/frontend-types';
import {
  holdScene,
  prefersReducedMotion,
  registerCapture,
  warnCaptureFailed,
  FADE_MS,
} from '../../../components/3D/planView/planHandover';
import { handOnLivePose } from '../../../components/3D/planView/planPoseHandback';
import { composeHeldPicture } from '../../../components/3D/planView/useHandoverJoin';
import { PLAN_REST_HEIGHT_SCALE } from '../../../components/3D/planView/planMapLock';
import {
  liveAnchor,
  matchesLiveFloor,
  prepareFloorFade,
  resetLiveScene,
  setFloorFade,
  setLiveHeightScale,
  setLiveSceneState,
} from '../../../components/3D/planView/liveEditFlightScene';
import {
  EDIT_ANNOUNCE_WAIT_MS,
  EDIT_CAPTURE_WAIT_MS,
  EDIT_FLIGHT_MS,
  EDIT_FLIGHT_PLAN_READ_TIMEOUT_MS,
  EDIT_HOLD_CAP_MS,
  editFlightDuration,
  editFlightHeightAt,
  editFlightReducer,
  restoreLiveView,
  editFlightUnderWay,
  initialEditFlight,
  type EditFlightEvent,
  type EditFlightOutcome,
  type EditFlightState,
} from './editFlight';
import { editFlightTarget, type EditFlightTarget } from './editFlightTarget';

type Handler = { enable(): void; disable(): void; isEnabled(): boolean };
const HANDLERS = [
  'dragPan',
  'scrollZoom',
  'boxZoom',
  'dragRotate',
  'keyboard',
  'doubleClickZoom',
  'touchZoomRotate',
  'touchPitch',
] as const;

const DEV = process.env.NODE_ENV !== 'production';
/**
 * Development builds only: a user-timing mark for the rig, carrying the live renderer's program count
 * at that instant (`window.__editFlight`), so the rig reads what compiled between two marks exactly.
 */
const mark = (name: string) => {
  if (!DEV || typeof performance === 'undefined' || !performance.mark) return;
  try {
    const probe = (window as unknown as { __editFlight?: { programs?: () => number } })
      .__editFlight;
    performance.mark(name, { detail: { programs: probe?.programs?.() ?? null } });
  } catch {
    // A browser without user timing.
  }
};

/** R8: one line per press, the twin's BID and the outcome. Never an address, a plan or a coordinate. */
const logOutcome = (twinBid: string, outcome: EditFlightOutcome) => {
  // eslint-disable-next-line no-console
  console.info('[edit-flight]', { twinBid, outcome });
};

const storage = (): Storage | null => {
  try {
    return window.localStorage;
  } catch {
    return null;
  }
};

/**
 * R3.4: the map takes no pan, zoom, rotate or tilt while the camera flies, and the twin drawn on it
 * takes no click or hover (its hitboxes would select, and the landing picture would show it); returns
 * the restore.
 */
export const lockMapInput = (map: MapboxMap): (() => void) => {
  const container = typeof map.getCanvasContainer === 'function' ? map.getCanvasContainer() : null;
  const pointerBefore = container?.style.pointerEvents ?? '';
  if (container) container.style.pointerEvents = 'none';
  const turnedOff: Handler[] = [];
  HANDLERS.forEach(name => {
    const handler = (map as unknown as Record<string, Handler | undefined>)[name];
    if (handler && handler.isEnabled()) {
      handler.disable();
      turnedOff.push(handler);
    }
  });
  return () => {
    turnedOff.forEach(h => h.enable());
    if (container) container.style.pointerEvents = pointerBefore;
  };
};

/**
 * The 3D label cards over the map (`Label3D`, drawn as HTML by Drei, so no floor fade reaches them)
 * are hidden with the panels for the flight; returns the restore. The landing picture is the canvas
 * alone, and the page leaves on landing, so only a cancel or a fallback restores them.
 */
export const hideMapLabels = (map: MapboxMap): (() => void) => {
  const container = typeof map.getContainer === 'function' ? map.getContainer() : null;
  const labels = Array.from(container?.querySelectorAll<HTMLElement>('.label-3d') ?? []);
  const before = labels.map(el => el.style.visibility);
  labels.forEach(el => {
    el.style.visibility = 'hidden';
  });
  return () =>
    labels.forEach((el, i) => {
      el.style.visibility = before[i];
    });
};

export interface EditFlightInputs {
  twinBid: string | null;
  twinName: string | null;
  accessToken: string | null;
  middlewareApi: string;
  getMap: () => MapboxMap | null;
  /** What the page watches for a cancel (R9): any change while a flight runs stops it. */
  watched: readonly unknown[];
}

export interface EditFlight {
  /** Edit was pressed with onboarding's address for the loaded twin. */
  start: (target: string) => void;
  /** Stop a flight under way (R9). */
  cancel: () => void;
  state: EditFlightState;
  /** The panels over the map are hidden (R5). */
  panelsHidden: boolean;
  /** What the polite live region says now (criterion 11.4). */
  announcement: string;
}

export const useEditFlight = ({
  twinBid,
  twinName,
  accessToken,
  middlewareApi,
  getMap,
  watched,
}: EditFlightInputs): EditFlight => {
  const navigate = useNavigate();
  const location = useLocation();
  const [state, setState] = useState<EditFlightState>(initialEditFlight);
  const stateRef = useRef(state);
  const [announcement, announce] = useReducer((_: string, next: string) => next, '');
  /** What a cancel or a fallback must undo: the map's input, the read's abort, the timers. */
  const run = useRef<{
    unlock: (() => void) | null;
    showLabels: (() => void) | null;
    abort: AbortController | null;
    timer: ReturnType<typeof setTimeout> | null;
    offRender: (() => void) | null;
    target: string | null;
    twinBid: string | null;
  }>({
    unlock: null,
    showLabels: null,
    abort: null,
    timer: null,
    offRender: null,
    target: null,
    twinBid: null,
  });

  const dispatch = useCallback((event: EditFlightEvent) => {
    const next = editFlightReducer(stateRef.current, event);
    stateRef.current = next;
    setState(next);
    return next;
  }, []);

  const tidy = useCallback(() => {
    const r = run.current;
    r.abort?.abort();
    r.abort = null;
    if (r.timer !== null) clearTimeout(r.timer);
    r.timer = null;
    r.offRender?.();
    r.offRender = null;
  }, []);

  /** The press could not fly: the panels back, the scene restored, and Edit navigates as today. */
  const fallBack = useCallback(
    (outcome: EditFlightOutcome) => {
      const r = run.current;
      tidy();
      restoreLiveView(getMap(), r.unlock);
      r.unlock = null;
      r.showLabels?.();
      r.showLabels = null;
      if (r.twinBid) logOutcome(r.twinBid, outcome);
      const target = r.target;
      r.target = null;
      dispatch({ type: 'reset' });
      if (target) navigate(target);
    },
    [dispatch, getMap, navigate, tidy],
  );

  const cancel = useCallback(() => {
    if (!editFlightUnderWay(stateRef.current.phase)) return;
    const r = run.current;
    tidy();
    restoreLiveView(getMap(), r.unlock);
    r.unlock = null;
    r.showLabels?.();
    r.showLabels = null;
    r.target = null;
    dispatch({ type: 'cancel' });
    if (r.twinBid) logOutcome(r.twinBid, 'cancelled');
    announce('Editing cancelled');
    dispatch({ type: 'reset' });
  }, [dispatch, getMap, tidy]);

  /** R6: the camera has landed. Capture once inside a render, hold, hand on, navigate. */
  const land = useCallback(
    (generation: number, map: MapboxMap, bid: string) => {
      if (dispatch({ type: 'landed', generation }).phase !== 'landed') return;
      setLiveHeightScale(PLAN_REST_HEIGHT_SCALE);
      setFloorFade(0);
      mark('edit-flight:landed');
      const finish = (ok: boolean) => {
        const r = run.current;
        r.offRender?.();
        r.offRender = null;
        if (r.timer !== null) clearTimeout(r.timer);
        r.timer = null;
        // The page leaves now: the hidden labels go with it.
        r.showLabels = null;
        const next = dispatch({ type: 'captured', generation, ok });
        if (next.phase !== 'navigated') return;
        try {
          const container = map.getContainer().getBoundingClientRect();
          const centre = map.getCenter();
          handOnLivePose({
            twinBid: bid,
            center: [centre.lng, centre.lat],
            zoom: map.getZoom(),
            bearing: map.getBearing(),
            at: {
              x: container.left + container.width / 2,
              y: container.top + container.height / 2,
            },
          });
        } catch {
          // A map torn down in this frame: Floor Plan opens on its own pose, and Edit still navigates.
        }
        r.unlock?.();
        r.unlock = null;
        logOutcome(bid, next.outcome ?? 'flew');
        const target = r.target;
        r.target = null;
        if (target) navigate(target);
        dispatch({ type: 'reset' });
      };
      // R6.2: the live map keeps no drawing buffer, so the picture is read inside its own render.
      const onRender = () => {
        let held = false;
        try {
          const picture = composeHeldPicture(map);
          if (picture) {
            const host = () =>
              document.querySelector<HTMLElement>('[data-onboarding-backdrop]') ?? null;
            const withdraw = registerCapture(() => ({ ...picture, host }));
            holdScene({
              announceWaitMs: EDIT_ANNOUNCE_WAIT_MS,
              capMs: EDIT_HOLD_CAP_MS,
              origin: 'edit',
              twinBid: bid,
            });
            withdraw();
            held = true;
            mark('edit-flight:captured');
          }
        } catch {
          warnCaptureFailed('canvas-unreadable');
        }
        finish(held);
      };
      map.once('render', onRender);
      run.current.offRender = () => map.off('render', onRender);
      // A map that never draws again would leave the panels hidden and the map locked: the wait is
      // bounded, and a cancel clears it with the other timers.
      run.current.timer = setTimeout(() => {
        run.current.timer = null;
        finish(false);
      }, EDIT_CAPTURE_WAIT_MS);
      map.triggerRepaint();
    },
    [dispatch, navigate],
  );

  const start = useCallback(
    (target: string) => {
      if (editFlightUnderWay(stateRef.current.phase)) return;
      const map = getMap();
      const bid = twinBid;
      const next = dispatch({ type: 'press' });
      const generation = next.generation;
      const r = run.current;
      r.target = target;
      r.twinBid = bid;
      mark('edit-flight:press');
      announce(`Opening ${twinName ?? 'the twin'} for editing`);
      const reduced = prefersReducedMotion();
      if (!map || !bid || !accessToken) {
        // Nothing to read with (no live map, twin or sign-in yet): the plan went unread.
        dispatch({ type: 'fail', generation, outcome: 'plan-unread' });
        fallBack('plan-unread');
        return;
      }
      r.unlock = lockMapInput(map);
      r.showLabels = hideMapLabels(map);

      // The plan read: both reads in one 1500 ms budget, aborted at its end (Failure States).
      const abort = new AbortController();
      r.abort = abort;
      r.timer = setTimeout(() => {
        r.timer = null;
        abort.abort();
        if (dispatch({ type: 'planTimeout', generation }).phase === 'fallback') {
          fallBack('plan-timeout');
        }
      }, EDIT_FLIGHT_PLAN_READ_TIMEOUT_MS);

      const read = async (): Promise<FloorPlannerLayout | null | 'failed'> => {
        const plans = await getTwinPlans(middlewareApi, accessToken, bid, { signal: abort.signal });
        if (plans === undefined) return 'failed';
        if (plans.length === 0) return null;
        // Out of time, cancelled or overtaken: a late list answer starts no second read.
        if (abort.signal.aborted) return 'failed';
        // The plan onboarding resumes: the highest id (`usePlanLoad`'s plan read).
        const newest = plans.slice().sort((a, b) => b.id - a.id)[0];
        const plan = await getTwinPlanById(middlewareApi, accessToken, newest.id, {
          signal: abort.signal,
        });
        if (!plan) return 'failed';
        return (plan.layout as unknown as FloorPlannerLayout) ?? null;
      };

      // A read that rejects is a read that failed. Anything that throws after it (the target, the
      // floors, the flight) must never leave the panels hidden and the map locked: the scene is
      // restored and Edit navigates, as for any flight that cannot run.
      void read()
        .catch((): 'failed' => 'failed')
        .then(async layout => {
          if (stateRef.current.generation !== generation || stateRef.current.phase !== 'reading') {
            return;
          }
          if (r.timer !== null) clearTimeout(r.timer);
          r.timer = null;
          r.abort = null;
          if (layout === 'failed') {
            if (dispatch({ type: 'planFailed', generation }).phase === 'fallback') {
              fallBack('plan-unread');
            }
            return;
          }
          const remembered = readPlanView(storage(), bid);
          const { origin, orientationDeg } = liveAnchor();
          const canvas = map.getCanvas();
          const found: EditFlightTarget = editFlightTarget({
            layout,
            rememberedFloorId: remembered.ok ? remembered.view?.floorId : undefined,
            twinBid: bid,
            origin,
            orientationDeg,
            mapSize: { width: canvas.clientWidth, height: canvas.clientHeight },
          });
          const floorMatched = found.kind === 'target' && matchesLiveFloor(found.floorBids);
          const after = dispatch({
            type: 'planRead',
            generation,
            target: found.kind,
            floorMatched,
          });
          if (after.phase === 'fallback') {
            fallBack(after.outcome ?? 'no-plan');
            return;
          }
          if (after.phase !== 'flying' || found.kind !== 'target') return;

          // R4.3: the other floors see-through and built before the camera moves. R4.4: none match,
          // every floor flattens and none fades.
          if (floorMatched) prepareFloorFade(found.floorBids, { cut: reduced });
          setLiveSceneState('entering');

          // `flyToTarget` slows the flight itself by `?slowFlight=`; the ease reads the same factor, so
          // the heights land with the camera at any speed.
          const duration = editFlightDuration(window.location.search);
          // The clock starts when the camera does: the helper may first wait for the map's style.
          let startedAt: number | null = null;
          const onMoveStart = () => {
            startedAt ??= performance.now();
          };
          const onRender = () => {
            if (startedAt === null) return;
            const t = (performance.now() - startedAt) / duration;
            setLiveHeightScale(reduced ? PLAN_REST_HEIGHT_SCALE : editFlightHeightAt(t));
            if (floorMatched && !reduced) {
              setFloorFade(1 - Math.min(1, (performance.now() - startedAt) / FADE_MS));
            }
          };
          if (reduced) setLiveHeightScale(PLAN_REST_HEIGHT_SCALE);
          map.on('movestart', onMoveStart);
          map.on('render', onRender);
          r.offRender = () => {
            map.off('movestart', onMoveStart);
            map.off('render', onRender);
          };
          // A cancel aborts the flight even while it still waits for the map (R9).
          const flight = new AbortController();
          r.abort = flight;
          // A spotlight still flying may have its entity flight queued behind the one this flight
          // aborts; it is no longer current, so it never starts (`chainAfterPredictiveFlight`), as
          // "Show whole twin" does.
          useSpotlightStore.getState().clearSpotlight();
          mark('edit-flight:moving');
          const { camera } = found;
          await flyToTarget(
            {
              kind: 'position',
              longitude: camera.center[0],
              latitude: camera.center[1],
              zoom: camera.zoom,
              bearing: camera.bearing,
              pitch: 0,
            },
            { map, duration: EDIT_FLIGHT_MS, signal: flight.signal },
          );
          // Read afresh: the flight took time, and a cancel may have moved the state on.
          const landedOn: EditFlightState = stateRef.current;
          if (landedOn.generation !== generation || landedOn.phase !== 'flying') return;
          r.offRender?.();
          r.offRender = null;
          land(generation, map, bid);
        })
        .catch(() => {
          if (
            dispatch({ type: 'fail', generation, outcome: 'flight-failed' }).phase === 'fallback'
          ) {
            fallBack('flight-failed');
          }
        });
    },
    [accessToken, dispatch, fallBack, getMap, land, middlewareApi, twinBid, twinName],
  );

  // R9: leaving the page, or a change of twin, organisation, mode or route, stops a flight under way.
  // `location.pathname` is watched with the page's own list; a navigation the flight itself makes
  // happens after its state has moved on, so it cancels nothing.
  const watchKey = [location.pathname, ...watched];
  const firstWatch = useRef(true);
  useEffect(() => {
    if (firstWatch.current) {
      firstWatch.current = false;
      return;
    }
    if (editFlightUnderWay(stateRef.current.phase)) cancel();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, watchKey);
  // Leaving the page cancels a flight under way, and always leaves the live scene as a customer
  // sees it: the map is going, and the next live viewer must not open flat.
  const cancelRef = useRef(cancel);
  cancelRef.current = cancel;
  useEffect(
    () => () => {
      cancelRef.current();
      resetLiveScene();
    },
    [],
  );

  // R9: a press anywhere in the nav bar while the flight runs stops it.
  const underWay = editFlightUnderWay(state.phase);
  useEffect(() => {
    if (!underWay) return undefined;
    const nav = document.getElementById('main-nav');
    if (!nav) return undefined;
    const onPress = (e: Event) => {
      if (e instanceof KeyboardEvent && e.key !== 'Enter' && e.key !== ' ') return;
      // The mode switcher: a second Edit does nothing (R3.4), and another mode cancels through the
      // mode watch above.
      if (e.target instanceof Element && e.target.closest('.mode-switcher')) return;
      cancel();
    };
    // `click` too: assistive technology can activate a control with a click alone.
    nav.addEventListener('pointerdown', onPress, true);
    nav.addEventListener('keydown', onPress, true);
    nav.addEventListener('click', onPress, true);
    return () => {
      nav.removeEventListener('pointerdown', onPress, true);
      nav.removeEventListener('keydown', onPress, true);
      nav.removeEventListener('click', onPress, true);
    };
  }, [underWay, cancel]);

  return {
    start,
    cancel,
    state,
    panelsHidden: underWay,
    announcement,
  };
};
